/**
 * @file Checks whether an open or closed project's funding or
 * distribution bank account is invalid.
 */
import { PROJECT_STATES } from "@components/Domain";

export type BankAccount = {
  id: string;
  isInvalid?: boolean;
};

export type Project = {
  state?: string;
  bankAccountId?: string;
  distributionBankAccountId?: string;
};

/**
 * Returns whether any open project's funding account, or any open or
 * closed project's distribution account, is among the invalid ones.
 */
const hasInvalidBankAccounts = (
  bankAccounts: BankAccount[] = [],
  projects: Project[] = [],
): boolean => {
  const invalidBankAccountsIds = bankAccounts
    .filter((item) => item.isInvalid)
    .map((item) => item.id);

  const invalidFundingAccountProjects = projects
    .filter(({ state }) => state === PROJECT_STATES.OPEN)
    .filter(({ bankAccountId }) => {
      const hasInvalidFundingAccount = !!bankAccountId
        && invalidBankAccountsIds.includes(bankAccountId);

      return hasInvalidFundingAccount;
    });

  const invalidDistributionsAccountProjects = projects
    .filter(({ state }) => {
      const isOpen = state === PROJECT_STATES.OPEN;
      const isClosed = state === PROJECT_STATES.CLOSED;
      const isOpenOrClosed = isOpen || isClosed;

      return isOpenOrClosed;
    })
    .filter(({ distributionBankAccountId }) => {
      const hasInvalidDistributionsAccount = !!distributionBankAccountId
        && invalidBankAccountsIds.includes(distributionBankAccountId);

      return hasInvalidDistributionsAccount;
    });

  const result = [
    ...invalidFundingAccountProjects,
    ...invalidDistributionsAccountProjects,
  ].length > 0;

  return result;
};

export default hasInvalidBankAccounts;
